import type { StrictLocalizeFunction } from "@faire/localization/core/types";
import { useStrictLocalization } from "@faire/localization/react/useStrictLocalization";
import { cn } from "@faire/react/tools/cn";
import React from "react";

import { SVGIcon } from "./SVGIcon";
import { SVGIconComponent, SVGIconProps } from "./types";

// NOTE: This needs to stay a function component otherwise downstream icon tooling will not be able
// to find icon nodes via ts-morph since it expects a function node. (We can't switch to variable node)

export function createSVGIcon(
  path: React.ReactNode,
  displayName: string,
  defaultProps?:
    | ((strictLocalize: StrictLocalizeFunction) => SVGIconProps)
    | SVGIconProps
): SVGIconComponent {
  const Component = React.memo(
    ({ ...props }: SVGIconProps & React.RefAttributes<SVGElement>) => {
      const { strictLocalize } = useStrictLocalization();
      const unwrappedDefaultProps =
        typeof defaultProps === "function"
          ? defaultProps?.(strictLocalize)
          : defaultProps;

      const { className: defaultClass, titleAccess } =
        unwrappedDefaultProps ?? {};
      const { className: explicitClass } = props ?? {};

      return (
        <SVGIcon
          titleAccess={titleAccess}
          {...unwrappedDefaultProps}
          {...props}
          className={cn(defaultClass, explicitClass)}
        >
          {path}
        </SVGIcon>
      );
    }
  );

  if (process.env.NODE_ENV !== "production") {
    Component.displayName = `${displayName}Icon`;
  }

  return Component;
}
